import PropTypes from "prop-types";
import { Box, TextField, Typography } from "@mui/material";
import { DISABLED_OUTLINED_INPUT_SX } from "utils/disabledFieldStyles";
import { SEG_LIMIT, fmtSeconds, getSegments, segDurationState } from "utils/segmentAnnotation";

export default function SegmentAnnotationFields({
  referenceText,
  splitTimes,
  duration,
  segments,
  onSegmentChange,
  disabled,
  t,
}) {
  const segs = getSegments(splitTimes, duration);

  return (
    <Box>
      <Box
        sx={{
          bgcolor: "#FAFAF8",
          border: "1px solid #E2E0DA",
          borderRadius: 1,
          p: 1.25,
          mb: 1.5,
          display: "flex",
          gap: 1.25,
          alignItems: "flex-start",
        }}
      >
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: "#A09D97",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            flexShrink: 0,
            pt: 0.25,
          }}
        >
          {t("annotator.annotationPage.split.referencePassage", "Reference passage")}
        </Typography>
        <Typography sx={{ fontSize: "12px", lineHeight: 1.5, color: "#1A1917", flex: 1 }}>
          {referenceText}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          color: "#A09D97",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mb: 0.75,
        }}
      >
        {t(
          "annotator.annotationPage.split.segmentsHint",
          "Segments — paste the matching reference portion and type what was actually said"
        )}
      </Typography>

      {segs.map((seg, segIdx) => {
        const dur = seg.end - seg.start;
        const st = segDurationState(dur);
        const data = segments[segIdx] || { reference_text: "", transcription_text: "" };
        const borderColor = st === "over" ? "#FCA5A5" : st === "warn" ? "#FCD34D" : "#E2E0DA";
        const headBg = st === "over" ? "#FEF2F2" : st === "warn" ? "#FFFBEB" : "#F0EFEB";

        return (
          <Box
            key={segIdx}
            sx={{
              border: `1px solid ${borderColor}`,
              borderRadius: 1,
              mb: 0.75,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.625,
                bgcolor: headBg,
                fontFamily: "monospace",
                fontSize: "11px",
              }}
            >
              <Box
                sx={{
                  fontWeight: 600,
                  fontSize: "10px",
                  px: 0.625,
                  py: 0.125,
                  borderRadius: 0.375,
                  bgcolor: "#fff",
                  border: `1px solid ${borderColor}`,
                  color: st === "over" ? "#DC2626" : st === "warn" ? "#B45309" : "#6B6860",
                }}
              >
                {t("annotator.annotationPage.split.segLabel", "Seg {{n}}", { n: segIdx + 1 })}
              </Box>
              <Typography
                component="span"
                sx={{ color: "#A09D97", fontFamily: "monospace", fontSize: "11px" }}
              >
                {fmtSeconds(seg.start)}–{fmtSeconds(seg.end)}
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: "#1A1917",
                  fontWeight: 500,
                  fontFamily: "monospace",
                  fontSize: "11px",
                }}
              >
                {fmtSeconds(dur)}
              </Typography>
              {st === "over" && (
                <Typography
                  component="span"
                  sx={{ color: "#DC2626", ml: "auto", fontSize: "11px", fontFamily: "sans-serif" }}
                >
                  {t("annotator.annotationPage.split.overLimit", "⚠ {{seconds}}s over", {
                    seconds: String(Math.ceil(dur - SEG_LIMIT)),
                  })}
                </Typography>
              )}
              {st === "warn" && (
                <Typography
                  component="span"
                  sx={{ color: "#B45309", ml: "auto", fontSize: "11px", fontFamily: "sans-serif" }}
                >
                  {t("annotator.annotationPage.split.nearLimit", "Near limit")}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1,
                p: 1.25,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#6B6860",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.375,
                  }}
                >
                  {t("annotator.annotationPage.split.reference", "Reference")}{" "}
                  <Box component="span" sx={{ color: "#DC2626" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={data.reference_text || ""}
                  disabled={disabled}
                  placeholder={t(
                    "annotator.annotationPage.split.referencePlaceholder",
                    "Paste the reference portion…"
                  )}
                  onChange={(e) => onSegmentChange(segIdx, "reference_text", e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": { fontSize: "12px", lineHeight: 1.45 },
                    ...(disabled ? DISABLED_OUTLINED_INPUT_SX : {}),
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#6B6860",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.375,
                  }}
                >
                  {t("annotator.annotationPage.split.annotated", "Annotated")}{" "}
                  <Box component="span" sx={{ color: "#DC2626" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={data.transcription_text || ""}
                  disabled={disabled}
                  placeholder={t(
                    "annotator.annotationPage.split.annotatedPlaceholder",
                    "Type what was said…"
                  )}
                  onChange={(e) => onSegmentChange(segIdx, "transcription_text", e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": { fontSize: "12px", lineHeight: 1.45 },
                    ...(disabled ? DISABLED_OUTLINED_INPUT_SX : {}),
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

SegmentAnnotationFields.propTypes = {
  referenceText: PropTypes.string,
  splitTimes: PropTypes.arrayOf(PropTypes.number).isRequired,
  duration: PropTypes.number.isRequired,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      reference_text: PropTypes.string,
      transcription_text: PropTypes.string,
    })
  ).isRequired,
  onSegmentChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

SegmentAnnotationFields.defaultProps = {
  referenceText: "",
  disabled: false,
};
